import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { brandReviewSchema, buildBrandReviewSchema } from '../data/reviews';

/**
 * Per-page SEO. Uses Helmet for meta tags only; bypasses Helmet for <title>
 * and JSON-LD <script> blocks (which Helmet rejects via JSX expressions) by
 * mutating the document head directly in useEffect.
 */
const DEFAULT_TITLE = 'Royal Garden Villas | Luxury Private Pool Villas in Lonavala';
const DEFAULT_DESC =
  'Premium 3BHK to 12BHK private pool villas in Lonavala. Family weekends, corporate retreats, intimate celebrations. Book direct with instant confirmation.';

// Module-level cache so we only hit /api/public/brand-review-stats once per
// browser session — not once per page navigation. Google crawls a page a few
// seconds after load so the fetched number lands in time for schema parsing.
let _brandStatsCache = null;
let _brandStatsPromise = null;

const fetchBrandStats = () => {
  if (_brandStatsCache) return Promise.resolve(_brandStatsCache);
  if (_brandStatsPromise) return _brandStatsPromise;
  const api = process.env.REACT_APP_BACKEND_URL;
  if (!api) return Promise.resolve(null);
  _brandStatsPromise = fetch(`${api}/api/public/brand-review-stats`)
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (d && d.rating_value && d.review_count) _brandStatsCache = d;
      return _brandStatsCache;
    })
    .catch(() => null);
  return _brandStatsPromise;
};

const SEO = ({
  title,
  description,
  path = '/',
  image,
  breadcrumbs = [],
  jsonLd = [],
  skipBrandReview = false,
}) => {
  const [liveBrand, setLiveBrand] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchBrandStats().then((stats) => {
      if (alive && stats) {
        setLiveBrand(buildBrandReviewSchema({ ratingValue: stats.rating_value, reviewCount: stats.review_count }));
      }
    });
    return () => { alive = false; };
  }, []);
  const origin = 'https://royalgardenvillas.in';
  const canonical = `${origin}${path}`;
  const ogImage = image || 'https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/bqiogpix_RG%20Banner.png';
  const finalTitle = (title || DEFAULT_TITLE).toString();
  const finalDesc = (description || DEFAULT_DESC).toString();

  // Update document title directly (Helmet has known issues with JSX <title> children)
  useEffect(() => { document.title = finalTitle; }, [finalTitle]);

  // Drop index.html's homepage fallback meta/canonical so each page exposes exactly one set.
  useEffect(() => {
    document.querySelectorAll('[data-static-seo]').forEach((n) => n.parentNode && n.parentNode.removeChild(n));
  }, []);

  // Inject JSON-LD schemas via direct DOM (Helmet has known issues with JSX <script> children)
  useEffect(() => {
    const breadcrumbSchema = breadcrumbs.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: b.name,
            item: `${origin}${b.url}`,
          })),
        }
      : null;
    // Build the list of schemas to emit. The brand Product is injected into
    // index.html already (so non-JS crawlers see it). On mount we UPDATE that
    // existing node in-place with the live-blended count instead of appending
    // a duplicate — duplicates with the same @id trigger Google Rich Results
    // "multiple aggregate ratings" errors.
    const base = [];
    if (breadcrumbSchema) base.push(breadcrumbSchema);
    const all = [...base, ...jsonLd];
    const created = all.map((data) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.dataset.rgvSeo = '1';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    // Update the brand Product node (index.html hardcodes a baseline; replace
    // its text content with the live-blended schema when stats arrive).
    if (!skipBrandReview) {
      const node = document.querySelector('script[data-brand-product="true"]');
      const brand = liveBrand || brandReviewSchema;
      // Page-specific Product carrying the same live rating, so every page is eligible for review stars.
      const payload = {
        ...brand,
        '@id': `${canonical}#product`,
        url: canonical,
        name: finalTitle.replace(/\s*\|\s*Royal Garden Villas\s*$/, ''),
        description: finalDesc,
        image: ogImage,
      };
      if (node) {
        node.textContent = JSON.stringify(payload);
      } else {
        // Fallback: no index.html node exists yet (e.g. during dev HMR) — append.
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-brand-product', 'true');
        s.dataset.rgvSeo = '1';
        s.textContent = JSON.stringify(payload);
        document.head.appendChild(s);
        created.push(s);
      }
    }

    return () => {
      created.forEach((s) => { try { document.head.removeChild(s); } catch (e) { /* noop */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(breadcrumbs), JSON.stringify(jsonLd), skipBrandReview, liveBrand, canonical, finalTitle, finalDesc, ogImage]);

  return (
    <Helmet>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={canonical} />

      {/* OpenGraph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default SEO;
